import * as Services from '~/api/services';
import { toast } from '~/composables/useToast';
import { Logger, Router } from '~/core';
import { TranslatableError } from '~/errors';
import { cleanData } from '~/plugins/clean';
import { useLoadingStore } from '~/stores';

// Services with this key will not be included in verbose logging ("submitting/calling request..." etc)
const filteredVerboseLoggingServices = ['steamProfile@get'];

interface ServiceOptions {
    displayErrorsInUI?: boolean | string;
    background?: boolean;
}

function convertToLoadingId(id: string, args: any[]) {
    let result = id;

    const valueToString = (value: any) => {
        if ([null, undefined].includes(value)) return '';

        return value.toString();
    };
    const objectToString = (data: Record<string, any>) => {
        let result = '';

        for (const index in data) {
            const value = data[index];

            result += `${index}:${typeof value === 'object' ? objectToString(value) : valueToString(value)},`;
        }

        return result.substring(0, result.length - 1);
    };

    for (const index in args) {
        // TODO: possibly some type of order enforcing for args?
        const value = args[index];
        if (value === undefined) continue;

        result += `;${typeof value === 'object' ? objectToString(value) : valueToString(value)}`;
    }

    return result;
}

function useService<T>(id: string, options: ServiceOptions | boolean | string, ...args: any[]): Promise<T> {
    if (typeof options !== 'object') {
        options = {
            displayErrorsInUI: options,
        };
    }
    const { displayErrorsInUI } = options;

    if (displayErrorsInUI) {
        //toast.dismiss();
    }

    const cleanArgs = args.map((arg) => {
        if (typeof arg === 'object') return cleanData(arg);

        return arg;
    });

    if (!filteredVerboseLoggingServices.includes(id)) Logger.debug('Service', `Calling ${id}`, ...cleanArgs);

    const [fullService, method] = id.split('@');
    let [namespace, service] = fullService.split(':');
    if (namespace && !service) {
        // Guess namespace
        service = namespace;
        namespace = (<string>Router.currentRoute.value.name).split('.')[0] === 'admin' ? 'admin' : 'client';
    }

    const serviceName = `${service.charAt(0).toUpperCase()}${service.slice(1)}Service`;

    const serviceClass = (Services as { [key: string]: any })[namespace]?.[serviceName];
    if (!serviceClass?.[method]) {
        const error = `Failed to get service with ID of '${id}': service ${serviceClass ? `method ${method}` : `class ${serviceName}`} not found?`;
        Logger.error('Service', error);
        if (displayErrorsInUI) {
            toast.error(['generic.something_went_wrong']);
        }
        throw new Error(error);
    }

    if (!filteredVerboseLoggingServices.includes(id)) Logger.debug('Service', `Submitting request to ${id}...`);

    const output = serviceClass[method](...args);
    if (output instanceof Promise) {
        return new Promise((resolve, reject) => {
            const promise = (options as ServiceOptions).background
                ? new Promise<() => void>((resolve) => resolve(() => {}))
                : new Promise<() => void>((resolve) => {
                      const loadingStore = useLoadingStore();
                      const finished = loadingStore.add(convertToLoadingId(id, cleanArgs));
                      resolve(finished);
                  });
            promise.then((finished) => {
                output
                    .then((res) => resolve(res))
                    .catch((err: Error) => {
                        if (err instanceof TranslatableError) {
                            if (displayErrorsInUI) {
                                const displayError = err.getDisplayError();
                                toast.error(displayError.title, displayError.messages);
                            }

                            return reject(err);
                        }

                        console.error(err);

                        Logger.error('Service', `Request ${id} failed:\n${err}`);
                        if (displayErrorsInUI) {
                            toast.error(['generic.something_went_wrong']);
                        }

                        reject(err);
                    })
                    .finally(() => {
                        finished();
                    });
            });
        });
    } else {
        return new Promise((resolve) => resolve(output));
    }
}

export default useService;

(window as any).useService = useService;
